import { useEffect, useMemo, useState } from 'react'
import { Badge } from '@klear/ui/components/badge'
import { Checkbox } from '@klear/ui/components/checkbox'
import { Loader2 } from 'lucide-react'
import { trpc } from '@klear/ui/lib/trpc'
import type { CollectionStepCtx } from '@/app/collections/outlet-context'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import type { Money } from '@klear/money'
import { KlearDate } from '@klear/date'
import * as changeCase from 'change-case'
import type { Fee } from '@klear/fee'
import type { FeeAllocationAdjustmentListItem } from '@klear/fee/wire'

type RowStatus = 'none' | 'preview' | 'saved'

function allocationChanged(original: Fee, next: Fee | undefined): boolean {
  if (next == null) return false
  return !next.klearAmount.equals(original.klearAmount) || !next.spvAmount.equals(original.spvAmount)
}

function AllocationCell({
  original,
  display,
  showComparison,
  isLoading,
  bold
}: {
  original: Money
  display: Money | undefined
  showComparison: boolean
  isLoading: boolean
  bold?: boolean
}) {
  const changed = display != null && !display.equals(original)

  if (!showComparison) {
    return <span className={bold ? 'font-medium' : undefined}>{original.toString()}</span>
  }

  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-muted-foreground">{original.toString()}</span>
        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
      </span>
    )
  }

  if (display == null) {
    return <span className={bold ? 'font-medium' : undefined}>{original.toString()}</span>
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={changed ? 'line-through text-muted-foreground' : undefined}>{original.toString()}</span>
      {changed && <span className="text-primary font-medium">{display.toString()}</span>}
    </span>
  )
}

function StatusBadge({ status }: { status: RowStatus }) {
  if (status === 'saved') {
    return <Badge variant="default">Saved</Badge>
  }
  if (status === 'preview') {
    return <Badge variant="secondary">On post</Badge>
  }
  return <span className="text-muted-foreground">—</span>
}

function FeeRow({
  fee,
  checked,
  onToggle,
  recoveryDate,
  offerDisplay,
  savedAdjustment
}: {
  fee: Fee
  checked: boolean
  onToggle: () => void
  recoveryDate: string
  offerDisplay: string
  savedAdjustment: FeeAllocationAdjustmentListItem | undefined
}) {
  const id = fee.id!
  const isSaved = savedAdjustment != null

  const { data: recalcData, isLoading: recalcLoading } = trpc.fee.recalculateAllocation.useQuery(
    { feeId: id, recoveryDate },
    { enabled: checked && recoveryDate.length > 0 && !isSaved, staleTime: 30_000 }
  )

  const displayKlear = isSaved ? fee.klearAmount.add(savedAdjustment.klearDelta) : recalcData?.klearAmount
  const displayFunder = isSaved ? fee.spvAmount.add(savedAdjustment.spvDelta) : recalcData?.spvAmount
  const displayTotal = displayKlear != null && displayFunder != null ? displayKlear.add(displayFunder) : undefined

  const showComparison = checked && (isSaved || recalcData != null)
  const status: RowStatus = isSaved ? 'saved' : allocationChanged(fee, recalcData) ? 'preview' : 'none'
  const originalTotal = fee.klearAmount.add(fee.spvAmount)

  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/30">
      <td className="px-3 py-2 text-center">
        <Checkbox checked={checked} onCheckedChange={onToggle} />
      </td>
      <td className="px-3 py-2">{offerDisplay}</td>
      <td className="px-3 py-2">{changeCase.capitalCase(fee.type ?? 'fee')}</td>
      <td className="px-3 py-2">{fee.endDate?.toString() ?? '—'}</td>
      <td className="px-3 py-2">
        <StatusBadge status={status} />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <AllocationCell
          original={fee.klearAmount}
          display={displayKlear}
          showComparison={showComparison}
          isLoading={!isSaved && recalcLoading}
        />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <AllocationCell
          original={fee.spvAmount}
          display={displayFunder}
          showComparison={showComparison}
          isLoading={!isSaved && recalcLoading}
        />
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        <AllocationCell
          original={originalTotal}
          display={displayTotal}
          showComparison={showComparison}
          isLoading={!isSaved && recalcLoading}
          bold
        />
      </td>
    </tr>
  )
}

export default function RecalculateAllocationStep({ ctx }: WizardStepComponentProps<CollectionStepCtx>) {
  const { state, invoiceOfferDisplayMap } = ctx
  const [recoveryDate] = useState<string>(() => KlearDate.today().toJSON())
  const [checkedFeeIds, setCheckedFeeIds] = useState<Set<number>>(new Set())

  const parentEventIds = useMemo(() => state.selectedRecoveries.map((r) => r.orderEventId), [state.selectedRecoveries])

  const { data: feeListData, isLoading } = trpc.fee.list.useQuery(
    { filters: { parentEventId: parentEventIds } },
    { enabled: parentEventIds.length > 0 }
  )

  const fees = useMemo(() => [...(feeListData ?? [])].sort((a, b) => a.parentId - b.parentId), [feeListData])

  const feeIds = useMemo(() => fees.flatMap((fee) => (fee.id != null ? [fee.id] : [])), [fees])

  const { data: savedAdjustmentsData } = trpc.fee.listAllocationAdjustments.useQuery(
    { filters: { feeIds } },
    { enabled: feeIds.length > 0, staleTime: 30_000 }
  )
  const savedAdjustments: FeeAllocationAdjustmentListItem[] = savedAdjustmentsData ?? []

  const savedAdjustmentByFeeAndDate = useMemo(() => {
    const map = new Map<string, FeeAllocationAdjustmentListItem>()
    for (const adjustment of savedAdjustments) {
      map.set(`${adjustment.feeId}:${adjustment.recoveryDate}`, adjustment)
    }
    return map
  }, [savedAdjustments])

  useEffect(() => {
    if (fees.length > 0 && checkedFeeIds.size === 0) {
      setCheckedFeeIds(new Set(fees.filter((f) => f.id != null).map((f) => f.id!)))
    }
  }, [fees])

  const toggleFee = (feeId: number) => {
    setCheckedFeeIds((prev) => {
      const next = new Set(prev)
      if (next.has(feeId)) next.delete(feeId)
      else next.add(feeId)
      return next
    })
  }

  const savedCount = useMemo(
    () => feeIds.filter((feeId) => savedAdjustmentByFeeAndDate.has(`${feeId}:${recoveryDate}`)).length,
    [feeIds, recoveryDate, savedAdjustmentByFeeAndDate]
  )

  return (
    <div className="space-y-4 w-full">
      <p className="text-sm text-muted-foreground">
        Review fee allocations for the selected recoveries. Allocation adjustments are recalculated for today and saved
        automatically when the recovery payment posts.
      </p>

      {savedCount > 0 && (
        <p className="text-sm text-muted-foreground">
          {savedCount} fee{savedCount === 1 ? '' : 's'} already adjusted for today
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 h-[300px] text-sm text-muted-foreground border rounded-md">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading fees…
        </div>
      ) : fees.length === 0 ? (
        <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground border rounded-md">
          No fees found for the selected recoveries.
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-10 px-3 py-2" />
                <th className="px-3 py-2 text-left font-medium">Invoice offer</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">End date</th>
                <th className="px-3 py-2 text-left font-medium">Adjustment</th>
                <th className="px-3 py-2 text-right font-medium">Klear</th>
                <th className="px-3 py-2 text-right font-medium">Funder</th>
                <th className="px-3 py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {fees.map((fee) => {
                if (fee.id == null) return null
                return (
                  <FeeRow
                    key={fee.id}
                    fee={fee}
                    checked={checkedFeeIds.has(fee.id)}
                    onToggle={() => toggleFee(fee.id!)}
                    recoveryDate={recoveryDate}
                    offerDisplay={invoiceOfferDisplayMap.get(fee.parentId) ?? `#${fee.parentId}`}
                    savedAdjustment={savedAdjustmentByFeeAndDate.get(`${fee.id}:${recoveryDate}`)}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
